import React, {useState, useRef, useEffect} from 'react'
import {VisDiv} from '../components'
import { useOfflineContext } from '../context/OfflineContext'
import { useStateContext } from '../context/StateContext'
import { useUploadPhoto, mutationCall } from '../hooks/mutations'
import { useAuthContext } from '../hooks/useAuthContext'
import { successAlert, errorAlert, fromBase64ToSanity, keyId, objectIsObject, unique } from '../lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import TestButton from './frontend_components/TestButton'
import Swal from 'sweetalert2'
import { isGodMode } from '../lib/userPermissions'

/**
 * OfflineUpdate: This component is used to update the system while offline.
 * It takes care of downloading images, updating the register and the devices, 
 * and saving the data to the server.
 * 
 * It also provides a button to download the offline data as a JSON file.
 * 
 * The component will work even if the user navigates away from it.
 * 
 * The component uses React Query to update the cache of the registers.
 * #offlineUpdate
 * The component uses the useOfflineContext hook to access the offline data.
 * 
 * The component uses the useMutation hook to update the register and the devices.
 * 
 * The component uses the useUploadPhoto hook to upload the images.
 * @param {Object} data - The data passed from the parent component.
 * @param {Object} data.registersData - The data of the registers.
 */
const OfflineUpdate = ({data={}}) => {
    
    const {registersData} = data
    const {offlineData, setOfflineData, base64Images, setBase64Images, bypassOffline, setBypassOffline,  failedDevicesQueryDocs, setFailedDevicesQueryDocs,} = useOfflineContext()
    const {
        sUser,
        site,
        register,
        setRegister,
        currentDevice,
        setCurrentDevice,
        registers, 
        setRegisters,
        setLoading
    } = useStateContext();

    const logContainerRef = useRef(undefined);
    const [updateLogs, setUpdateLogs] = useState([])
    const queryClient = useQueryClient()
    const [sanityPictures, setSanityPictures] = useState([])

    // Auto-scroll to latest log
    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [updateLogs]);

    const updateLogText = (text="Update Eseguito con successo", status = "success" ) => {
        const emoji = {
            success: "✅",
            error: "❌",
            warning: "⚠️",
            info: "ℹ️",
            processing: "⏳",
            photo: "📸",
            saving: "💾",
            network: "🌐",
        }
        
        return {
            log: <div style={{
                color: status === "success" ? "green" : 
                      status === "error" ? "red" : 
                      status === "warning" ? "orange" : 
                      "blue",
                padding: "5px",
                margin: "5px 0",
                fontSize: "14px",
                animation: "fadeIn 0.3s ease-in"
            }}>
                {emoji[status] || emoji.info} {text}
            </div>,
            status,
            text,
            timestamp: new Date().getTime()
        }
    }
    
    const updateLog = (text="Update Eseguito con successo", status = "success" ) => {
        setUpdateLogs(prevLogs => [...!Array.isArray(prevLogs) ? [] : prevLogs, updateLogText(text, status)])
    }

    const deleteOfflineData = () => {
    Swal.fire({
        title:  "Eliminare I File Offline?",
        text: "Spedire l'errore o scaricare il file con i dati offline prima di eliminare. Chiama se non sei sicuro",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'red',
        cancelButtonColor: 'grey',
        confirmButtonText: "ELIMINA",
        cancelButtonText: "ANNULLA"
        }).then((result) => {
        if (result.isConfirmed) {
            //setOfflineData([])
            setFailedDevicesQueryDocs([])
            updateLog("Dati offline eliminati", "info") 
        }
        })

    }
        const bypassOfflineFn = () => {
            setBypassOffline(!bypassOffline)
        }

    const downloadOfflineData = (offlineRegisters) => {
        updateLog("Avvio download dei dati...", "processing")
        const jsonString = JSON.stringify(offlineRegisters, undefined, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'offlineRegisters.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        updateLog("Download dei dati completato", "success")
    }

    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    
    // Custom offline mutation that doesn't use error boundary
    const offlineCreateReplace = ({...props}) => {
        return useMutation(
            (data) => mutationCall(data, {
                route: `${apiUrl}/calls/create-replace`, 
                user, 
                onError: (e) => {
                    // Don't use showBoundary here - just log the error
                    console.log("REGISTER Not UPDATED", e)
                }
            }),
            {
            networkMode: 'always',
            retry: false,
            ...props
            }
        )
    }
    
    const {mutate: updateRegister} = offlineCreateReplace({
        onError:(e)=> {
            console.log("REGISTER Not UPDATED", e)
            // Don't throw the error here - let the individual calls handle it
            // This prevents the component from crashing
        }
    })

    const {mutate: uploadPhotoAsset} = useUploadPhoto({

        onError:(e)=>{
            console.error("salvataggio immagine non riuscito:",e.message)
            //errorAlert("UPLOAD FOTO NON RIUSCITO")
            updateLog("Errore nel caricamento della foto", "error")
            throw e
        }
    })

    const extractBase64Images = (register) => {
        const commentsWithImages = !Array.isArray(register?.devices) ? [] : register?.devices?.map?.((device)=>{
            return device?.comments
        }).flat().filter?.((x)=>x?.images?.length > 0)
        
        const commentsWithBase64Images = !Array.isArray(commentsWithImages) ? [] : commentsWithImages?.filter?.((x)=>
            x?.images?.some?.((y)=>y?.before?.base64Image || y?.after?.base64Image))
        
        const allBase64Images = !Array.isArray(commentsWithBase64Images) ? [] : commentsWithBase64Images?.map?.((item)=>{
            const images = !Array.isArray(item?.images) ? [] : item?.images?.map?.((image)=> {
                const beforeBase64Image = image?.before?.base64Image ? image?.before : undefined
                const afterBase64Image = image?.after?.base64Image ? image?.after : undefined
                return [beforeBase64Image, afterBase64Image]?.filter?.((x)=>x)
            })
            return images?.flat()
        })?.flat()

        return allBase64Images || []
    }

    const uploadImages = async (images) => {
        const uploadedImages = []
        
        for (const imageDoc of images) {
            try {
                const file = await fromBase64ToSanity(imageDoc?.base64Image?.src)
                const asset = {fileType:"image", file: file?.file}
                
                await new Promise((resolve, reject) => {
                    uploadPhotoAsset(asset, {
                        onSuccess: (data) => {
                            //custom test post 7 error
/*                             if(imageDoc?.beforeAfterPhotoId ==="e341d974-505f-4490-b7c8-973e65cb6ebf"){
                                throw new Error('custom test post 7 error')
                            } */
                            const sanityDoc = {
                                _type: 'image',
                                _key: keyId(),
                                asset: {
                                    _type: 'reference',
                                    _ref: data?._id,
                                    _key: keyId()
                                }
                            }
                            uploadedImages.push({
                                sanityImage: sanityDoc,
                                beforeAfterPhotoId: imageDoc?.beforeAfterPhotoId,
                                beforeOrAfter: imageDoc?.beforeOrAfter
                            })
                            resolve()
                        },
                        onError: reject
                    })
                })
            } catch (error) {
                console.error("Failed to upload image:", error)
                throw error
            }
        }
        
        return uploadedImages
    }

    const updateRegisterWithImages = (register, uploadedImages) => {
        if(!register) {
            updateLog(`Errore registro non trovato ${register?.label || register?._id}`, "error")
            return
        } 
        const newDevices = !Array.isArray(register?.devices) ? [] : register?.devices?.map?.((device)=>{
            const commentsArray = device?.comments ? device?.comments : []
            const updatedComments = !Array.isArray(commentsArray) ? [] : commentsArray?.map?.((comment)=>{
                const commentImages = !Array.isArray(comment?.images) ? [] : comment?.images
                const commentsWithNewImages = !Array.isArray(commentImages) ? [] : commentImages?.map?.((image)=>{
                    const findUpdatedImagesByBeforeAndAfterId = !Array.isArray(uploadedImages) ? [] : uploadedImages?.filter?.((x)=>
                        x?.beforeAfterPhotoId == image?._id)
                    const findNewBeforeImage = !Array.isArray(findUpdatedImagesByBeforeAndAfterId) ? undefined : findUpdatedImagesByBeforeAndAfterId?.find?.((y)=>
                        y?.beforeOrAfter == "before")
                    const findNewAfterImage = !Array.isArray(findUpdatedImagesByBeforeAndAfterId) ? undefined : findUpdatedImagesByBeforeAndAfterId?.find?.((y)=>
                        y?.beforeOrAfter == "after")
                    const newImageDoc = {
                        ... objectIsObject(image),
                        before: findNewBeforeImage?.sanityImage?.asset?._ref ? 
                            {...findNewBeforeImage, base64Image:""} : image?.before,
                        after: findNewAfterImage?.sanityImage?.asset?._ref ? 
                            {...findNewAfterImage, base64Image:""} : image?.after,
                    }
                    console.log(
                        "liuawehfliaweufh", findUpdatedImagesByBeforeAndAfterId, 
                        "beforeImageFound", findNewBeforeImage, 
                        "afterImageFound", findNewAfterImage,
                        "newImageDoc", newImageDoc
                     )
                    return newImageDoc
                })
                return {...comment, images: commentsWithNewImages}
            })
            return {...device, comments: updatedComments}
        })

        return {...register, devices: newDevices}
    }

    const updateRegisterInDB = async (register, successfulUpdates) => {
        return new Promise((resolve, reject) => {
            // Add timeout to prevent hanging requests
            const timeout = setTimeout(() => {
                updateLog(`⏰ Timeout per registro ${register.label || register._id}`, "error")
                reject(new Error("Request timeout"))
            }, 30000) // 30 second timeout

            updateRegister({doc: register}, {
                onSuccess: (data) => {
                    clearTimeout(timeout)
                    // Verify that we got a valid response with the expected data
                    if (data && data._id === register._id) {
                        updateLog(`✅ Conferma server per registro ${register.label || register._id}`, "success")
                        // Only add to successful updates here, when we have confirmed success
                        successfulUpdates.push(data)
                        updateLog(`Registro ${register.label || register._id} aggiornato con successo`, "success")
                        resolve(data)
                    } else {
                        updateLog(`❌ Risposta server non valida per registro ${register.label || register._id}`, "error")
                        reject(new Error("Invalid server response"))
                    }
                },
                onError: (error) => {
                    clearTimeout(timeout)
                    updateLog(`❌ Errore server per registro ${register.label || register._id}: ${error.message}`, "error")
                    reject(error)
                }
            })
        })
    }

    const updateUIState = (successfulUpdates) => {
        // Update current register if it was updated
        const currentUpdatedRegister = !Array.isArray(successfulUpdates) ? undefined : successfulUpdates?.find?.((x)=>x?._id == register?._id)
        const currentUpdatedDevice = !Array.isArray(currentUpdatedRegister?.devices) ? undefined : currentUpdatedRegister?.devices?.find?.((x)=>x?._id == currentDevice?._id)
        
        // Update registers list
        const filteredRegisters = !Array.isArray(registers) ? [] : registers?.filter?.((x)=>!successfulUpdates?.some?.((y)=>y?._id == x?._id))
        const updatedRegisters = [...filteredRegisters, ...successfulUpdates]
        
        // Update React Query cache
        queryClient.cancelQueries({ queryKey: ['registers', site] })
        queryClient.setQueryData(['registers', site], updatedRegisters)
        
        // Update state
        setRegister(currentUpdatedRegister)
        setCurrentDevice(currentUpdatedDevice)
        setRegisters(updatedRegisters)
    }

    const logFilter = (register) => {
        const queryRegister = (register) => !Array.isArray(registersData) ? undefined : registersData?.find?.(y=>y?._id == register?._id)
        const nLogs = (register) => !Array.isArray(register?.devices) ? [] : register?.devices?.map?.((device)=>{
            const deviceLogs = device?.userLog ? device?.userLog : []
            return deviceLogs
        }).flat().length
        
        const obj = {register: nLogs(register), queryRegister: nLogs(queryRegister(register))}
        console.log("djalkdjalksjd", obj )
            
        return obj.register > obj.queryRegister || !queryRegister(register) 
    }
    const allFailedDevicesRegisters = !Array.isArray(failedDevicesQueryDocs) ? [] : failedDevicesQueryDocs
    ?.map?.((x)=>{
        if(!Array.isArray(registersData)){
            console.log("registersData non trovata eoijfiowedfj", {registersData})
            return
        }
        const findRegister = registersData?.find?.((y)=>y?._id == x?.register?._ref)
        if(!findRegister){
            console.log("findRegister non trovata poewkdpokwd", {findRegister, x})
        }
        return findRegister })
        ?.filter?.(x=>x)
    //const failedDevicesRegisters = unique("_id", allFailedDevicesRegisters)

    const allOfflineData = unique("_id", allFailedDevicesRegisters)?.map((register)=>{
        // MERGE FAILED UPDATES IF _updatedAt IS THE SAME OR HIGHER
        const mergedDevices = !Array.isArray(register?.devices) ? [] : register?.devices?.map?.((device)=>{
            const deviceToUpdate = !Array.isArray(failedDevicesQueryDocs) ? undefined : failedDevicesQueryDocs?.find?.((x)=>x?._id == device?._id)
            if(!deviceToUpdate) return device
            console.log("deviceToUpdate jedhuwjed", {deviceToUpdate, device})
            if(deviceToUpdate?._updatedAt && !device?._updatedAt) return deviceToUpdate
            if(deviceToUpdate?.updatedAt < device?._updatedAt) return device
            return deviceToUpdate

        })
        const devicesInFailedDevicesQueryDocsNotInRegister = !Array.isArray(failedDevicesQueryDocs) ? [] : failedDevicesQueryDocs?.filter?.((x)=>!register?.devices?.some?.((y)=>y?._id == x?._id))?.filter((z)=>z?.register?._ref == register?._id)
        console.log("about to update o2ekjo2i3ej", {mergedDevices, devicesInDailedDevicesQueryDocsNorInRegister: devicesInFailedDevicesQueryDocsNotInRegister})
        return {...register, devices: [...mergedDevices, ...devicesInFailedDevicesQueryDocsNotInRegister]}
    })
   //const allOfflineData = unique("_id", allFailedDevicesRegisters)


    const testConsole = {registersData, allOfflineData, failedDevicesQueryDocs, allFailedDevicesRegisters }


    const updateOfflineData = async () => {
        setUpdateLogs([]) // Clear previous logs
        updateLog("Inizio aggiornamento dati offline", "info")
        let successfulUpdates = []
        let failedUpdates = []
        
        try {
            for (const register of allOfflineData) {
                try {
                    await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for visual feedback
                    updateLog(`Elaborazione registro: ${register.label || register._id}`, "processing")
                    
                    const base64Images = extractBase64Images(register)
                    
                    if(base64Images?.length > 0) {
                        updateLog(`Caricamento ${base64Images.length} foto`, "photo")
                        const uploadedImages = await uploadImages(base64Images)
                        updateLog("Foto caricate con successo", "success")
                        
                        const updatedRegister = updateRegisterWithImages(register, uploadedImages)
                        
                        if(updatedRegister) {
                            updateLog("Salvataggio registro con foto", "saving")
                            await updateRegisterInDB(updatedRegister, successfulUpdates)
                        } else {
                            throw new Error("Failed to process register with images")
                        }
                    } else {
                        updateLog("Salvataggio registro", "saving")
                        await updateRegisterInDB(register, successfulUpdates)
                    }
                } catch (error) {
                    console.error("Failed to update register:", register._id, error)
                    failedUpdates.push(register)
                    updateLog(`Errore nell'aggiornamento del registro ${register.label || register._id}`, "error")
                }
            }

            updateUIState(successfulUpdates)
            setFailedDevicesQueryDocs(failedDevicesQueryDocs?.map?.((item)=>{
                const isFailedRegister = !Array.isArray(failedUpdates) ? undefined : failedUpdates?.some(x=>x?._id == item?.register?._ref)
                if(isFailedRegister) {
                    return  item
                }
                return undefined

            })?.filter(x=>x))
            
            //setOfflineData(failedUpdates)
            
            if(failedUpdates.length > 0) {
                updateLog(`⚠️ ${failedUpdates.length} registri non aggiornati`, "warning")
                errorAlert("Alcuni aggiornamenti non sono riusciti. I registri non riusciti sono rimasti nei dati offline.", {failedUpdates})
            } else {
                updateLog("✨ Tutti i registri aggiornati con successo", "success")
                successAlert("All updates completed successfully")
                bypassOfflineFn()
                //window.location.reload()
            }

        } catch (error) {
            console.error("Update process failed:", error)
            errorAlert("Update process failed")
            updateLog("Processo di aggiornamento fallito", "error")
        }
    }

    return (
        <div className="products-heading stop-scroll-refresh" >
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>
            <TestButton consoleData={testConsole} message="Aggiorna dati offline" buttonId='alksdjlksd8'  />
            <VisDiv style={{marginBottom:"10px"}}>
                <button style={{backgroundColor:"green"}} 
                        className='btn' 
                        onClick={()=>updateOfflineData()}>
                    AGGIORNA DATI OFFLINE
                </button>
            </VisDiv>  
            <VisDiv style={{marginBottom:"10px"}}>
                <button style={{backgroundColor:"blue"}} 
                        className='btn' 
                        onClick={()=>downloadOfflineData(allOfflineData)}>
                    SCARICA DATI OFFLINE (JSON)
                </button>
            </VisDiv>
            <VisDiv rules={[]} style={{marginBottom:"10px"}}>
                <button style={{backgroundColor:"orange"}} 
                        className='btn' 
                        onClick={()=>bypassOfflineFn()}>
                    CONTINUA A LAVORARE
                </button>
            </VisDiv>
            <h3>SONO STATI RILEVATI DATI CARICATI IN MODALITA' OFFLINE. 
                SE HAI BUONA CONNESSIONE, CLICCA QUI SOPRA PER AGGIORNARE IL SISTEMA.</h3>
            <p> SE TI TROVI IN UN' AREA CON CONNETTIVITA' INSTABILE, 
               PUOI CONTINUARE A LAVORARE OFFLINE METTENDO IL DISPOSITIVO IN MODALITA' AEREO 
               E AGGIORNARE IL SISTEMA UNA VOLTA RAGGIUNTA UN'AREA CON CONNESSIONE STABILE </p>
            <h3>REGISTRI DA AGGIORNARE:</h3>
            {allOfflineData?.map?.((item)=>{
                return (
                    <div key={item?._id} > 
                        📄 {`${item?.label} ${item.registerName ? item.registerName : ""}`}
                    </div>
                )
            })}
            <div 
                ref={logContainerRef}
                style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    padding: "10px",
                    margin: "10px 0",
                    border: "1px solid #eee",
                    borderRadius: "5px",
                    backgroundColor: "#fafafa"
                }}
            >
                {!Array.isArray(updateLogs) ? [] : updateLogs?.map?.((x, index)=><div key={`${index}-${x.timestamp}`}>{x?.log}</div>)}
            </div>
            <VisDiv rules={[]} style={{marginBottom:"10px"}}>
                <button style={{backgroundColor:"red"}} 
                        className='btn' 
                        onClick={()=>deleteOfflineData()}>
                    ELIMINA I FILE OFFLINE
                </button>
            </VisDiv>
        </div>
    )
}

export default OfflineUpdate
